import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { TimeRange } from "@/types/enums";

interface OvertimeRequestWithReason {
  id: string;
  department: string;
  justification: string;
  date: string;
  overtime_reasons?: {
    id: string;
    main_title: string;
    reason: string | null;
  } | {
    id: string;
    main_title: string;
    reason: string | null;
  }[];
}

interface ReasonAnalytics {
  id: string;
  category: string;
  reason: string;
  count: number;
  percentage: number;
}

interface UseOvertimeReasonsAnalyticsProps {
  selectedDepartment?: string;
  startDate?: string;
  endDate?: string;
}

export function useOvertimeReasonsAnalytics(props?: UseOvertimeReasonsAnalyticsProps) {
  const { selectedDepartment, startDate, endDate } = props || {};
  const { user } = useAuth();
  const [requests, setRequests] = useState<OvertimeRequestWithReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch overtime requests with joined overtime_reasons
  useEffect(() => {
    const fetchOvertimeRequestsWithReasons = async () => {
      if (!user?.companyId) return;

      setIsLoading(true);
      setError(null);

      try {
        let query = supabase
          .from('overtime_requests')
          .select(`
            id,
            department,
            justification,
            date,
            overtime_reasons!justification(
              id,
              main_title,
              reason
            )
          `)
          .not('justification', 'is', null); // Only get requests that have a justification

        // Filter by company through the overtime_reasons relation
        if (user.companyId) {
          // We need to filter by company_id through the overtime_reasons table
          const { data: companyReasons } = await supabase
            .from('overtime_reasons')
            .select('id')
            .eq('company_id', user.companyId)
            .eq('is_active', true);

          if (companyReasons && companyReasons.length > 0) {
            const reasonIds = companyReasons.map((r: any) => r.id);
            query = query.in('justification', reasonIds);
          } else {
            // No reasons for this company, return empty
            setRequests([]);
            setIsLoading(false);
            return;
          }
        }

        // Add date filtering if provided
        if (startDate) {
          query = query.gte('date', startDate);
        }
        if (endDate) {
          query = query.lte('date', endDate);
        }

        const { data, error } = await query;

        if (error) {
          throw new Error(`Failed to fetch overtime requests: ${error.message}`);
        }

        setRequests(data || []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error fetching overtime reasons analytics:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOvertimeRequestsWithReasons();
  }, [user?.companyId, startDate, endDate]);

  // Get unique departments
  const departments = useMemo(() => {
    const unique = new Set<string>();
    requests.forEach(req => {
      if (req.department?.trim()) {
        unique.add(req.department.trim());
      }
    });
    return Array.from(unique).sort();
  }, [requests]);

  // Process analytics data with department filtering
  const reasonsAnalytics = useMemo((): ReasonAnalytics[] => {
    if (!requests.length) return [];

    // Filter by department if selected
    let filteredRequests = requests;
    if (selectedDepartment && selectedDepartment !== TimeRange.ALL) {
      filteredRequests = requests.filter(req => req.department === selectedDepartment);
    }

    if (!filteredRequests.length) return [];

    // Group by reason
    const reasonCounts = filteredRequests.reduce((acc, request) => {
      if (!request.overtime_reasons) return acc;

      // Handle both object and array formats from Supabase join
      const reasonData = Array.isArray(request.overtime_reasons) 
        ? request.overtime_reasons[0] 
        : request.overtime_reasons;
      
      if (!reasonData) return acc;

      const key = `${reasonData.id}`;
      
      if (!acc[key]) {
        acc[key] = {
          id: reasonData.id,
          category: reasonData.main_title?.trim() || 'Uncategorized',
          reason: reasonData.reason?.trim() || 'No reason specified',
          count: 0
        };
      }
      acc[key].count += 1;
      return acc;
    }, {} as Record<string, Omit<ReasonAnalytics, 'percentage'>>);

    // Convert to array and calculate percentages
    const total = filteredRequests.length;
    return Object.values(reasonCounts)
      .map(item => ({
        ...item,
        percentage: (item.count / total) * 100
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [requests, selectedDepartment]);

  return {
    reasonsAnalytics,
    departments,
    isLoading,
    error,
    totalRequests: requests.length
  };
}
